import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  Plus,
  Cpu,
  Factory,
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MapPin,
  X,
  Pencil,
  Trash2,
  Loader2,
} from "lucide-react";
import { machinesAPI } from "../api";
import clsx from "clsx";
import toast from "react-hot-toast";
import { PERMISSIONS } from "../constants/roles";
import HasPermission from "../components/HasPermission";
import AddMachine from "./AddMachine";

const statusConfig = {
  operational: {
    label: "Operational",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    dot: "bg-emerald-500",
    iconBg: "bg-emerald-50 text-emerald-600 border border-emerald-100",
  },
  maintenance: {
    label: "In Maintenance",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
    iconBg: "bg-amber-50 text-amber-600 border border-amber-100",
  },
  error: {
    label: "Error",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
    iconBg: "bg-rose-50 text-rose-600 border border-rose-100",
  },
  offline: {
    label: "Offline",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
    iconBg: "bg-slate-100 text-slate-600 border border-slate-200",
  },
};

/**
 * Renders a single machine as a clean, modern card.
 */
const MachineCard = ({ machine, index, onEdit, onDelete }) => {
  const status = statusConfig[machine.status] || statusConfig.offline;
  const activeAlerts = Number(machine.active_alerts) || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.03, 0.3) }}
      className="h-full"
    >
      <Link
        to={`/machines/${machine.id}`}
        className="group bg-white rounded-2xl border border-slate-200/80 p-5 hover:border-blue-300 hover:shadow-md transition-all duration-200 flex flex-col justify-between h-full relative block cursor-pointer"
      >
        <div>
          {/* Header: Icon, Name, Model & Edit/Delete Actions */}
          <div className="flex items-start justify-between gap-2.5 mb-4">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div
                className={clsx(
                  "w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105",
                  status.iconBg
                )}
              >
                <Cpu className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors text-sm sm:text-base leading-snug break-words">
                  {machine.name}
                </h3>
                <p className="text-xs text-slate-500 font-medium mt-0.5 break-words">
                  {machine.model || machine.serial_number || "Machine Unit"}
                </p>
                <p className="text-xs text-blue-600 font-medium mt-1 break-words">
                  {machine.plant_name || "Plant not assigned"}
                </p>
              </div>
            </div>

            {/* Action Buttons: Edit & Delete */}
            <div className="flex items-center gap-1 flex-shrink-0 -mr-1 -mt-0.5">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit(machine);
                }}
                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit Machine"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(machine);
                }}
                className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                title="Delete Machine"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Machine Info Grid: Manufacturer & Plant Location */}
          <div className="grid grid-cols-2 gap-2.5 mb-2">
            <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-2.5">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5 flex items-center gap-1">
                <Factory className="w-3 h-3 text-slate-400 flex-shrink-0" />
                <span>Manufacturer</span>
              </p>
              <p className="text-xs font-semibold text-slate-700 break-words">
                {machine.manufacturer || "OEM Generic"}
              </p>
            </div>
            <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-2.5">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                <span>Location</span>
              </p>
              <p className="text-xs font-semibold text-slate-700 break-words">
                {machine.location_in_plant || "Main Facility"}
              </p>
            </div>
          </div>
        </div>

        {/* Footer: Alert status on the left & Status badge replacing View Details button on the right */}
        <div className="pt-3.5 border-t border-slate-100 flex items-center justify-between gap-2 text-xs mt-auto">
          {activeAlerts > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200/90 py-1 px-2.5 rounded-xl">
              <AlertTriangle className="w-3.5 h-3.5 text-rose-600 flex-shrink-0" />
              <span>{activeAlerts} active alert{activeAlerts > 1 ? "s" : ""}</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span>No active alerts</span>
            </span>
          )}

          <span
            className={clsx(
              "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border flex-shrink-0 ml-auto",
              status.badge
            )}
          >
            {status.label}
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

const Machines = () => {
  const PAGE_SIZE = 12;
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [deletingMachine, setDeletingMachine] = useState(null);
  const filterRef = useRef(null);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id) => machinesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["machines"] });
      toast.success("Machine deleted successfully");
      setDeletingMachine(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Failed to delete machine");
    },
  });

  const handleDeleteConfirm = () => {
    if (deletingMachine) {
      deleteMutation.mutate(deletingMachine.id);
    }
  };

  const { data: responseData, isLoading } = useQuery({
    queryKey: ["machines", { search, status: statusFilter, page: currentPage }],
    queryFn: () =>
      machinesAPI
        .getAll({
          search,
          status: statusFilter || undefined,
          page: currentPage,
          limit: PAGE_SIZE,
        })
        .then((res) => res.data),
  });

  const rawMachines = Array.isArray(responseData?.data)
    ? responseData.data
    : Array.isArray(responseData)
      ? responseData
      : [];

  const paginationInfo = responseData?.pagination;
  const totalItems = paginationInfo?.total ?? rawMachines.length;
  const totalPages = paginationInfo?.pages ?? Math.ceil(totalItems / PAGE_SIZE);

  // Resilient slice in case backend returns all unpaginated items
  const displayedMachines =
    rawMachines.length > PAGE_SIZE
      ? rawMachines.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
      : rawMachines;

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filterRef]);

  const handleFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const selectedStatus = statusConfig[statusFilter] || {
    label: "All Status",
  };

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
            Machine Fleet Management
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Real-time equipment telemetry, operating statuses & fault monitoring
          </p>
        </div>

        <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
          <button
            type="button"
            onClick={() => setIsAddModalOpen(true)}
            className="btn btn-primary text-xs sm:text-sm py-2 px-3.5 shadow-md shadow-blue-500/20 whitespace-nowrap self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" />
            Add Machine
          </button>
        </HasPermission>
      </div>

      {/* Control Bar: Search & Status Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Search Bar */}
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search by equipment name, model..."
            className="w-full pl-9 pr-9 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-sm transition-all"
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filter Dropdown */}
        <div className="flex items-center gap-2">
          <div ref={filterRef} className="relative w-full sm:w-auto">
            <button
              onClick={() => setIsFilterOpen((prev) => !prev)}
              className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-sm hover:border-slate-400"
            >
              <div className="flex items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-slate-500" />
                {statusFilter && (
                  <div
                    className={clsx(
                      "w-2 h-2 rounded-full",
                      statusConfig[statusFilter]?.dot || "bg-slate-400"
                    )}
                  />
                )}
                <span className="font-semibold text-slate-700">{selectedStatus.label}</span>
              </div>
              <ChevronDown
                className={clsx(
                  "w-3.5 h-3.5 text-slate-500 transition-transform duration-200",
                  isFilterOpen && "rotate-180"
                )}
              />
            </button>
            <AnimatePresence>
              {isFilterOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-1.5 w-full sm:w-52 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-20"
                >
                  <button
                    onClick={() => handleFilterChange("")}
                    className={clsx(
                      "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                      statusFilter === ""
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <span>All Status</span>
                    {statusFilter === "" && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                  </button>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => handleFilterChange(key)}
                      className={clsx(
                        "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                        statusFilter === key
                          ? "bg-blue-50 text-blue-700 font-semibold"
                          : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className={clsx("w-2 h-2 rounded-full", config.dot)} />
                        <span>{config.label}</span>
                      </div>
                      {statusFilter === key && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Loading Skeletons */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3.5 animate-pulse"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-slate-200" />
                  <div className="space-y-1.5">
                    <div className="h-4 w-28 bg-slate-200 rounded" />
                    <div className="h-3 w-16 bg-slate-100 rounded" />
                  </div>
                </div>
                <div className="h-6 w-20 bg-slate-200 rounded-full" />
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="h-12 bg-slate-100 rounded-xl" />
                <div className="h-12 bg-slate-100 rounded-xl" />
              </div>
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                <div className="h-3.5 w-24 bg-slate-100 rounded" />
                <div className="h-3.5 w-16 bg-slate-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : displayedMachines && displayedMachines.length > 0 ? (
        /* Machine Cards Grid */
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5">
            {displayedMachines.map((machine, index) => (
              <MachineCard
                key={machine.id}
                machine={machine}
                index={index}
                onEdit={(m) => setEditingMachine(m)}
                onDelete={(m) => setDeletingMachine(m)}
              />
            ))}
          </div>

          {/* Pagination Controls - only shown after 12 items (totalItems > 12) */}
          {!isLoading && totalItems > PAGE_SIZE && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200/80">
              <p className="text-xs text-slate-500 font-medium">
                Showing{" "}
                <span className="font-semibold text-slate-700">
                  {(currentPage - 1) * PAGE_SIZE + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-slate-700">
                  {Math.min(currentPage * PAGE_SIZE, totalItems)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-700">{totalItems}</span>{" "}
                equipment
              </p>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  className={clsx(
                    "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                    currentPage <= 1
                      ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                      : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                  )}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Prev</span>
                </button>

                <div className="flex items-center gap-1">
                  {getPageNumbers().map((pageItem, idx) =>
                    pageItem === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageItem}
                        type="button"
                        onClick={() => setCurrentPage(pageItem)}
                        className={clsx(
                          "w-8 h-8 rounded-xl text-xs font-semibold transition-all",
                          currentPage === pageItem
                            ? "bg-blue-600 text-white shadow-sm shadow-blue-500/30 font-bold"
                            : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 shadow-xs"
                        )}
                      >
                        {pageItem}
                      </button>
                    )
                  )}
                </div>

                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  className={clsx(
                    "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                    currentPage >= totalPages
                      ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                      : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                  )}
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Empty State */
        <div className="p-10 sm:p-14 bg-white border border-dashed border-slate-300 rounded-2xl text-center flex flex-col items-center justify-center">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
            <Cpu className="w-7 h-7" />
          </div>
          <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
            No Equipment Found
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 max-w-sm mb-5">
            {search || statusFilter
              ? "No machines matched your current search and filter criteria. Try resetting the filters."
              : "No industrial machines are currently registered. Add your first equipment unit to begin live monitoring."}
          </p>
          {search || statusFilter ? (
            <button
              onClick={() => {
                setSearch("");
                setStatusFilter("");
                setCurrentPage(1);
              }}
              className="btn btn-secondary text-xs sm:text-sm py-2 px-4"
            >
              Reset Filters
            </button>
          ) : (
            <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(true)}
                className="btn btn-primary inline-flex text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20"
              >
                <Plus className="w-4 h-4" />
                Add Machine
              </button>
            </HasPermission>
          )}
        </div>
      )}

      {/* Add / Edit Machine Popup Modal */}
      <AddMachine
        isOpen={isAddModalOpen || Boolean(editingMachine)}
        machineToEdit={editingMachine}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingMachine(null);
        }}
      />

      {/* Delete Confirmation Modal */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {deletingMachine && (
              <div
                className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
                onClick={() => {
                  if (!deleteMutation.isPending) setDeletingMachine(null);
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="relative w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 space-y-4 my-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 border border-rose-100/80 flex items-center justify-center shrink-0">
                      <Trash2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                        Delete Equipment
                      </h2>
                    </div>
                  </div>

                  {/* Warning Description */}
                  <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
                    Are you sure you want to delete{" "}
                    <span className="font-semibold text-slate-800">
                      {deletingMachine.name}
                    </span>
                    ? This action cannot be undone. All telemetry logs, alerts, and operational history will be permanently erased.
                  </p>

                  {/* Action Buttons (Right-aligned) */}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      disabled={deleteMutation.isPending}
                      onClick={() => setDeletingMachine(null)}
                      className="btn btn-secondary text-xs sm:text-sm py-2 px-4 hover:border-slate-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={deleteMutation.isPending}
                      onClick={handleDeleteConfirm}
                      className="btn bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm py-2 px-4 shadow-md shadow-rose-500/20 inline-flex items-center gap-2 font-medium"
                    >
                      {deleteMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          <span>Delete Equipment</span>
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default Machines;
